import { type Component, Show, createSignal, onMount, onCleanup } from 'solid-js'
import { Icon } from '@iconify-icon/solid'
import type { NotificationItem } from './types'

const SEVERITY_CONFIG = {
  notify: {
    bg: 'bg-white',
    border: 'border-gray-200',
    icon: null,
    iconColor: '',
    barColor: 'bg-gray-400'
  },
  info: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    icon: 'heroicons:information-circle',
    iconColor: 'text-blue-500',
    barColor: 'bg-blue-400'
  },
  warn: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    icon: 'heroicons:exclamation-triangle',
    iconColor: 'text-amber-500',
    barColor: 'bg-amber-400'
  },
  error: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    icon: 'heroicons:exclamation-circle',
    iconColor: 'text-red-500',
    barColor: 'bg-red-400'
  },
  success: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    icon: 'heroicons:check-circle',
    iconColor: 'text-green-500',
    barColor: 'bg-green-400'
  },
  progress: {
    bg: 'bg-white',
    border: 'border-primary-200',
    icon: null, // custom spinner
    iconColor: 'text-primary-600',
    barColor: 'bg-primary-400'
  }
} as const

interface NotificationCardProps {
  readonly item: NotificationItem
  readonly onDismiss: (id: string) => void
}

const NotificationCard: Component<NotificationCardProps> = (props) => {
  const config = () => SEVERITY_CONFIG[props.item.severity]
  const [exiting, setExiting] = createSignal(false)

  const dismiss = (): void => {
    setExiting(true)
    setTimeout(() => props.onDismiss(props.item.id), 200)
  }

  // Auto-dismiss after TTL
  let timer: ReturnType<typeof setTimeout> | undefined
  onMount(() => {
    if (!props.item.sticky && props.item.ttl > 0) {
      timer = setTimeout(dismiss, props.item.ttl)
    }
  })
  onCleanup(() => {
    if (timer) clearTimeout(timer)
  })

  return (
    <div
      class={`w-96 rounded-xl border shadow-lg overflow-hidden transition-all duration-200 ${config().bg} ${
        config().border
      }`}
      classList={{
        'animate-fade-in-right': !exiting(),
        'opacity-0 translate-x-4 transition-all duration-200': exiting(),
        'cursor-pointer': !!props.item.onClick
      }}
      onClick={() => props.item.onClick?.()}
    >
      <div class="flex items-start gap-3 px-4 py-3">
        {/* Icon — h-5 matches text-sm leading-5 so it aligns with the first line */}
        <Show when={props.item.severity === 'progress'}>
          <div class="shrink-0 h-5 flex items-center">
            <div class="w-5 h-5 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin" />
          </div>
        </Show>
        <Show when={props.item.severity !== 'progress' && config().icon}>
          <div class={`shrink-0 h-5 flex items-center ${config().iconColor}`}>
            <Icon icon={config().icon!} class="text-[1.25rem]" />
          </div>
        </Show>

        {/* Message */}
        <div class="flex-1 min-w-0 text-sm leading-5 text-gray-900">{props.item.message}</div>

        {/* Dismiss */}
        <button
          type="button"
          class="shrink-0 w-7 h-7 rounded-md flex items-center justify-center border border-gray-200 text-gray-400 hover:text-gray-600 hover:bg-gray-100 hover:border-gray-300 transition-all duration-150"
          onClick={(e) => {
            e.stopPropagation()
            dismiss()
          }}
        >
          <Icon icon="heroicons:x-mark" class="text-[0.875rem]" />
        </button>
      </div>

      {/* TTL progress bar */}
      <Show when={!props.item.sticky && props.item.ttl > 0}>
        <div class="h-1 w-full bg-gray-100">
          <div
            class={`h-full ${config().barColor} rounded-r-full`}
            style={{
              animation: `shrink-bar ${props.item.ttl}ms linear forwards`
            }}
          />
        </div>
      </Show>
    </div>
  )
}

export default NotificationCard
